import {
  NOT_CURRENT_CART_ERROR,
  OCI_SESSION_ERROR,
  PROLIX_APPLICATION_EXCEPTION,
  PROLIX_ERROR_IDENTIFIER
} from '../../../common/types/prolixApplicationError';
import { useCartPositionsWithBlockedState } from './useCartPositionsWithBlockedState';
import { useCartStateSelector } from './useCartStateSelector';
import { useShowCartFailure } from '../CartProvider.tsx';

export type CartErrorDialogDescriptor =
  | 'CART_FAILURE'
  | 'BLOCKED_POSITIONS'
  | 'NOT_CURRENT_CART'
  | 'OCI_SESSION_ERROR'
  | 'PROLIX_APPLICATION_ERROR'
  | 'GENERIC_CART_ERROR'
  | 'LOGIN_EXPIRED'
  | 'CART_EXPIRED'
  | 'CSV_IMPORT_ERROR'
  | 'POSITION_INTENT_ERROR'
  | 'BACKEND_MESSAGE';

export function useCartErrorNotification(): {
  errorDialogContentDescriptor: CartErrorDialogDescriptor | null;
  messages: string[];
  traceId: string | undefined;
} {
  const showCartFailure = useShowCartFailure();
  const hasCartError = useCartStateSelector<boolean>(state => state?.value === 'CartError');
  const isCartExpired = useCartStateSelector<boolean>(state => state?.value === 'CartExpired');
  const isLoginExpired = useCartStateSelector<boolean>(state => state?.value === 'LoginExpired');
  const hasPositionError = useCartStateSelector<boolean>(state =>
    state?.matches({ ProcessingPositions: 'PositionIntentError' })
  );

  const isCsvImport = useCartStateSelector<boolean>(state => {
    return state?.context?.isCsvImport ?? false;
  });

  const cartMessages = useCartStateSelector(state => state?.context?.cartMessages) ?? [];

  // cartMessages with a prolixErrorIdentifier should be handled with a specific dialog/flow and not by showing the generic error message
  const messages =
    cartMessages
      ?.filter(m => !m.prolixErrorIdentifier)
      .map(m => m.messageText)
      .filter((m): m is string => m !== undefined) ?? [];

  const prolixErrorIdentifier = cartMessages?.find(m => m[PROLIX_ERROR_IDENTIFIER])?.[PROLIX_ERROR_IDENTIFIER];
  const traceId = [...cartMessages].reverse().find(m => m.traceId)?.traceId;

  const userHasBlockedPositionsDialogAcknowledged = useCartStateSelector<boolean>(state => {
    const ctx = state.context;
    const currentId = ctx.currentOrder?.id ?? null;
    return ctx.blockedPositionsDialogAcknowledgedForCartId === currentId;
  });

  const { hasPositionsWithBlockedState } = useCartPositionsWithBlockedState();
  const showPositionsBlockedDialog =
    hasCartError && hasPositionsWithBlockedState && !userHasBlockedPositionsDialogAcknowledged;

  // Die Reihenfolge der Regeln bestimmt die Priorität: der erste zutreffende Eintrag gewinnt
  const errorDialogRules: { when: boolean | undefined; descriptor: CartErrorDialogDescriptor }[] = [
    { when: showCartFailure, descriptor: 'CART_FAILURE' },
    { when: isLoginExpired, descriptor: 'LOGIN_EXPIRED' },
    { when: hasCartError && prolixErrorIdentifier?.includes(NOT_CURRENT_CART_ERROR), descriptor: 'NOT_CURRENT_CART' },
    { when: hasCartError && prolixErrorIdentifier?.includes(OCI_SESSION_ERROR), descriptor: 'OCI_SESSION_ERROR' },
    { when: isCartExpired, descriptor: 'CART_EXPIRED' },
    { when: isCsvImport && messages.length > 0, descriptor: 'CSV_IMPORT_ERROR' },
    {
      when: hasCartError && prolixErrorIdentifier?.includes(PROLIX_APPLICATION_EXCEPTION),
      descriptor: 'PROLIX_APPLICATION_ERROR'
    },
    { when: showPositionsBlockedDialog, descriptor: 'BLOCKED_POSITIONS' },
    { when: hasPositionError, descriptor: 'POSITION_INTENT_ERROR' },
    { when: hasCartError, descriptor: 'GENERIC_CART_ERROR' },
    { when: messages.length > 0, descriptor: 'BACKEND_MESSAGE' }
  ];

  const errorDialogContentDescriptor = errorDialogRules.find(rule => rule.when)?.descriptor ?? null;

  return { errorDialogContentDescriptor, messages, traceId };
}
